import type { Dispatch, SetStateAction } from 'react';

import type { Subscription } from '@/store/user/types';
import type { JSONObject } from '@/utils/types';

import type { PaymentElementsFormProps } from '.';

export type OneTimePurchase = {
	content_type: string;
	id: string;
	created_at: string;
	payment_processor: string;
	processor_purchase_id: string;
	program_id: string;
	purchased_at: string;
	updated_at: string;
	user_id: string;
};

export type PurchaseRequestResponse = Subscription | OneTimePurchase | Record<string, unknown>;

export const isSubscriptionResponse = (data: PurchaseRequestResponse): data is Subscription => {
	// Null-safe: a non-object 200 body (null, "", etc.) must return false, not throw a TypeError
	// on the `in` check. `fetchActiveSubscriptionWithPolling`'s requireValid path relies on this to
	// classify a malformed body as not-subscription-shaped (returned immediately, not polled past).
	return typeof data === 'object' && data !== null && 'valid' in data;
};

export const isOneTimePurchaseResponse = (data: PurchaseRequestResponse): data is OneTimePurchase => {
	// Positively discriminate on `processor_purchase_id`, the payment identifier every completed
	// one-time purchase carries. A subscription is identified by `valid` and lacks it; an
	// unexpected/empty 200 body (null, "", {}, [], or an error-shaped object) has neither, so it
	// returns false and falls through to *neither* success handler in useSubmitClassicPaymentRequest
	// — rather than the previous `!('valid' in data)`, which classified any non-`valid` object
	// (including `{}`/arrays) as a one-time purchase and could mis-fire `oneTimePurchaseSuccess`.
	// Null-safe (mirrors isSubscriptionResponse): the `typeof`/`!== null` guard
	// keeps the `in` check from throwing a TypeError on a non-object body.
	return typeof data === 'object' && data !== null && 'processor_purchase_id' in data;
};

export type RequiresActionResponse = {
	requiresAction: boolean;
	clientSecret: string;
	customerId: string;
	paymentIntentId: string;
	subscriptionId?: string;
};

export type UseSubmitPaymentRequestProps = {
	onPurchaseError: (
		errorMessageToDisplay: string,
		eventProps?: JSONObject,
		errorCode?: string | JSONObject,
	) => void;
	onPurchaseSuccess: (subscription: Subscription, eventProps?: JSONObject) => Promise<void>;
	onPurchaseSuccessOverride: PaymentElementsFormProps['onPurchaseSuccess'];
	pageName: PaymentElementsFormProps['pageName'];
	setIsLoading: Dispatch<SetStateAction<boolean>>;
};
